/*
 * <product-video>
 *
 * Replaces the generic <deferred-media> behaviour for product-page videos.
 * The default DeferredMedia class doesn't reliably start muted autoplay on
 * mobile Safari (cloneNode-ing a <video muted> tag doesn't always propagate
 * the `muted` property), and it also doesn't gracefully retry when a
 * play() promise is rejected by the browser's autoplay policy.
 *
 * This element:
 *   1. Lazily inserts the real <video> / <iframe> on first activation
 *      (either a tap on the big play button or a thumbnail activation via
 *      media-gallery.js → playActiveMedia()).
 *   2. Explicitly sets muted=true and playsInline=true on native <video>
 *      nodes before calling play(), which fixes iOS Safari.
 *   3. Rebuilds YouTube / Vimeo iframe sources to include the autoplay &
 *      mute flags if they weren't already there.
 *   4. Exposes .loadContent() so the existing media-gallery code (which
 *      looks for `.deferred-media`) keeps working — we inherit from
 *      HTMLElement but wear both class names.
 */
if (!customElements.get('product-video')) {
  class ProductVideo extends HTMLElement {
    constructor() {
      super();
      this.loaded = false;
      const poster = this.querySelector('[id^="Deferred-Poster-"]');
      if (!poster) return;

      poster.addEventListener('click', (event) => {
        event.preventDefault();
        this.loadContent(false);
      });
    }

    loadContent(focus = true) {
      if (typeof window.pauseAllMedia === 'function') window.pauseAllMedia();
      if (this.loaded || this.getAttribute('loaded')) return;

      const template = this.querySelector('template');
      if (!template) return;

      const fragment = template.content.cloneNode(true);
      const mediaEl = fragment.querySelector('video, iframe');
      if (!mediaEl) return;

      if (mediaEl.tagName === 'VIDEO') {
        this.#prepareVideo(mediaEl);
      } else if (mediaEl.tagName === 'IFRAME') {
        this.#prepareIframe(mediaEl);
      }

      this.appendChild(mediaEl);
      this.setAttribute('loaded', '');
      this.loaded = true;

      if (mediaEl.tagName === 'VIDEO') {
        this.#tryPlay(mediaEl);
      }

      if (focus) {
        try {
          mediaEl.focus({ preventScroll: true });
        } catch (_) {
          /* noop */
        }
      }
    }

    #prepareVideo(video) {
      video.muted = true;
      video.defaultMuted = true;
      video.setAttribute('muted', 'muted');
      video.playsInline = true;
      video.setAttribute('playsinline', 'playsinline');
      video.setAttribute('webkit-playsinline', 'webkit-playsinline');
      video.setAttribute('autoplay', 'autoplay');
      video.setAttribute('preload', 'auto');
    }

    #prepareIframe(iframe) {
      const src = iframe.getAttribute('src');
      if (!src) return;
      try {
        const url = new URL(src, window.location.origin);
        if (url.hostname.includes('youtube')) {
          url.searchParams.set('autoplay', '1');
          url.searchParams.set('mute', '1');
          url.searchParams.set('playsinline', '1');
          if (!url.searchParams.has('enablejsapi')) {
            url.searchParams.set('enablejsapi', '1');
          }
        } else if (url.hostname.includes('vimeo')) {
          url.searchParams.set('autoplay', '1');
          url.searchParams.set('muted', '1');
          url.searchParams.set('playsinline', '1');
        }
        iframe.setAttribute('src', url.toString());
      } catch (_) {
        /* URL parsing failed — leave src as-is. */
      }

      if (!iframe.hasAttribute('allow')) {
        iframe.setAttribute('allow', 'autoplay; fullscreen; picture-in-picture');
      }
      iframe.setAttribute('allowfullscreen', '');
    }

    #tryPlay(video) {
      const attempt = () => {
        const promise = video.play();
        if (promise && typeof promise.catch === 'function') {
          promise.catch(() => {
            /* If browser still blocks (very rare when muted), the native
               controls remain — the user can tap to play manually. */
          });
        }
      };

      if (video.readyState >= 2) {
        attempt();
      } else {
        video.addEventListener('loadedmetadata', attempt, { once: true });
        video.addEventListener('canplay', attempt, { once: true });
        attempt();
      }
    }
  }

  customElements.define('product-video', ProductVideo);
}
